import React, { useState, useEffect } from 'react';

import EventTracking from 'lib/EventTracking';

import BoxBody from 'components/BoxBody';
import { CancellationReasonsSelect } from 'coaching/components/CancellationReasonsSelect';
import Divider from 'components/Divider';
import FormElement from 'components/FormElement';
import Link from 'components/Link';
import LinkConstants from 'constants/LinkConstants';
import Row from 'components/Row';
import Text from 'components/Text';
import Textarea from 'components/Textarea';

import { getCoachTitle } from 'coaching/lib/nameFormat';

export type Props = {
  coachName?: string;
  eventPrefix?: string;
  wouldBeLateCancellation?: boolean;
  scheduleDescription?: React.ReactElement;
  onSelectCancellationReason?: (cancellationReason?: string) => void;
  onChangeCancellationDetails?: (details?: string) => void;
};

export default function CancelRecurringSession({
  coachName,
  eventPrefix,
  wouldBeLateCancellation,
  scheduleDescription,
  onSelectCancellationReason,
  onChangeCancellationDetails,
}: Props) {
  function trackKey(key: string) {
    eventPrefix && EventTracking.trackKey(`${eventPrefix}.${key}`);
  }

  useEffect(() => {
    trackKey('view');
  }, []);

  const [details, setDetails] = useState<string>('');

  function updateCancellationDetails(value: string) {
    setDetails(value);
    onChangeCancellationDetails?.(value);
  }

  const detailsLabel = (
    <Text>
      Tell us more <Text weight="normal">(Optional)</Text>
    </Text>
  );

  return (
    <>
      <BoxBody size="xsmall" flush>
        {scheduleDescription && (
          <Row size="xsmall">
            {scheduleDescription} <Divider />
          </Row>
        )}
        {wouldBeLateCancellation && (
          <Row size="small">
            <LateCancellationNotice coachName={coachName} />
          </Row>
        )}
        <CancellationReasonsSelect
          sessionType="recurringSchedule"
          changeState={(value: string) => onSelectCancellationReason?.(value)}
          eventPrefix={eventPrefix}
          userType="participant"
        />
        <div onFocus={_ => trackKey('tell_us_more.click')}>
          <FormElement
            data-test="cancellation-details-textarea"
            label={detailsLabel}
            element={Textarea}
            elementProps={{
              value: details,
              placeholder: 'Enter text',
            }}
            onValidChange={e => {
              updateCancellationDetails(e.target.value);
            }}
          />
        </div>
      </BoxBody>
    </>
  );
}

function LateCancellationNotice({ coachName }: { coachName: string | undefined }) {
  return (
    <div data-test="late-cancellation-notice">
      <Row size="small">
        <Text size="small">
          Your future sessions with {coachName ? getCoachTitle(coachName) : 'your coach'} will be
          cancelled.
        </Text>
      </Row>
      <BoxBody size="xsmall" dark rounded>
        <Row size="small">
          <Text>
            You have a session that starts within 24 hours. You will be charged for the full session
            amount.
          </Text>
        </Row>
        <Link weight="semibold" href={LinkConstants.COACHING_HELP_SCHEDULING} target="_blank">
          View our scheduling policy
        </Link>
      </BoxBody>
    </div>
  );
}
